// Story assets made while a scene is written (docs/story-assets.md). A scene request may let the story generator
// look at what the world already has -- each character's poses, the places, the objects -- and create what the
// requested scene needs and nothing else exists for, through the same `create` runners the World Builder uses.
// Each kind is allowed independently, with its own limit; an omitted or zero limit turns that kind off.
import { z } from "zod";

/** Request keys, one per kind of asset. */
export const STORY_ASSET_KINDS = ["poses", "characters", "items", "backdrops", "variations"] as const;
export type StoryAssetKind = (typeof STORY_ASSET_KINDS)[number];

/**
 * The most of each kind one scene request may create.
 *   poses       a new expression sheet (up to 16 poses) for an existing character
 *   characters  a new character with its portrait, details and standard pose sheet, added to the story's cast
 *   items       a new prop, wearable or piece of scenery, added to the story's world
 *   backdrops   a new place (backdrop art), added to the story
 *   variations  a named alternative picture of an existing item or backdrop (open / closed, at night...)
 */
export const STORY_ASSET_LIMITS: Readonly<Record<StoryAssetKind, number>> = {
  poses: 4,
  characters: 2,
  items: 4,
  backdrops: 2,
  variations: 4,
};

const limit = (kind: StoryAssetKind) => z.number().int().min(0).max(STORY_ASSET_LIMITS[kind]).optional();
export const zStoryAssetCreation = z
  .object({
    poses: limit("poses"),
    characters: limit("characters"),
    items: limit("items"),
    backdrops: limit("backdrops"),
    variations: limit("variations"),
    /** Artwork quality for everything created (default "fast"); see CREATE_IMAGE_OPTIONS. */
    quality: z.enum(["fast", "best", "pro", "openai"]).optional(),
    /** Live replies default to reuse; other scenes default to a bounded optional check. */
    planning: z.enum(["reuse", "optional", "required"]).optional(),
    /**
     * Characters made or given poses here perform in this story: a new character gets one performance sheet of 16
     * poses on C and no A sheet, and a new sheet for an existing character is a 16-pose performance sheet whose first
     * panel becomes the role's idle and speaking pose. The server sets it for scenes and songs.
     */
    performanceSheets: z.boolean().optional(),
  })
  .strict();
export type StoryAssetCreation = z.infer<typeof zStoryAssetCreation>;

export function storyAssetPlanning(policy: StoryAssetCreation | undefined, live = false) {
  return policy?.planning ?? (live ? "reuse" : "optional");
}

/** Each kind's allowance (0 when not allowed). */
export function storyAssetLimits(policy: StoryAssetCreation | undefined): Record<StoryAssetKind, number> {
  return Object.fromEntries(STORY_ASSET_KINDS.map((kind) => [kind, policy?.[kind] ?? 0])) as Record<
    StoryAssetKind,
    number
  >;
}

/** Whether a request lets the generator create anything at all. */
export function allowsStoryAssets(policy: StoryAssetCreation | undefined): boolean {
  return STORY_ASSET_KINDS.some((kind) => (policy?.[kind] ?? 0) > 0);
}

/** One planned asset and what became of it. */
export interface StoryAssetResult {
  id: string;
  kind: "poses" | "character" | "item" | "backdrop" | "variation";
  /** A short description for people ("Zia: tearful and hopeful faces"). */
  label: string;
  state: "created" | "failed" | "skipped";
  /** The character, item or backdrop it made or changed (cast role id, item id or backdrop slug). */
  targetId?: string;
  /** The new sheet letter or variation name. */
  key?: string;
  /** Firestore path of the created or changed object. */
  path?: string;
  error?: string;
  /** The accounting operation that created it. */
  operationId?: string;
}

/** The story's world after its assets changed: what a player needs to show the new cast, places and objects. */
export interface StoryWorldUpdate {
  cast: unknown[];
  backdropIds: string[];
  backdrops: Record<string, import("./backdrops").Backdrop>;
  worldItemIds?: string[];
}
